import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { attendancesApi, attendancesQueryKeys } from '../api';
import type { AttendanceFilters, ArriveDto, LeaveDto } from '../types';

// Hook for fetching attendances list
export function useAttendances(filters?: AttendanceFilters) {
  return useQuery({
    queryKey: attendancesQueryKeys.list(filters),
    queryFn: () => attendancesApi.getAll(filters),
  });
}

// Hook for fetching single attendance
export function useAttendance(id: number | string) {
  return useQuery({
    queryKey: attendancesQueryKeys.detail(id),
    queryFn: () => attendancesApi.getById(id),
    enabled: !!id,
  });
}

// Hook for checking active attendance
export function useActiveAttendance() {
  return useQuery({
    queryKey: attendancesQueryKeys.active(),
    queryFn: () => attendancesApi.getActive(),
  });
}

// Hook for arriving (check-in)
export function useArrive() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ArriveDto) => attendancesApi.arrive(data),
    onSuccess: () => {
      toast.success(t('arrivedSuccess'));
      queryClient.invalidateQueries({ queryKey: attendancesQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('arriveError', { error: 'Unknown error' })));
    },
  });
}

// Hook for leaving (check-out)
export function useLeave() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: LeaveDto) => attendancesApi.leave(data),
    onSuccess: () => {
      toast.success(t('leftSuccess'));
      queryClient.invalidateQueries({ queryKey: attendancesQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('leaveError', { error: 'Unknown error' })));
    },
  });
}
